import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'

import Image from '@src/quince-site-common/src/domains/Image'
import RichText from '@src/quince-site-common/src/domains/RichText'

import Swiper from '../swiper'
import { NAV_POSITION } from '../swiper/Swiper'

import ProductColumn from './product-comparision-column'
import styles from './product-comparison-section-new.module.scss'
import { ComparisionColumn } from './product-comparison-section-new.types'

export interface ProductComparisonProductNewProps {
  title: string
  titleLink: string
  rows: RichText[]
  image: Image | undefined
  isCurrent: boolean
}

export interface ProductComparisonTableNewProps {
  columns: ComparisionColumn[]
  className?: string | undefined
  numberOfColumns: number
}

const ProductComparisonTableNew = ({ columns, className, numberOfColumns }: ProductComparisonTableNewProps) => {
  const classes = classNames(styles.wrapper, className)
  const [maxWidth, setMaxWidth] = useState('')

  const comparisionTableRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // @i18n-ignore
    const containerWidth = `${comparisionTableRef.current ? comparisionTableRef.current.clientWidth : 0}px`
    setMaxWidth(`calc(${containerWidth} / ${numberOfColumns})`)
  }, [numberOfColumns])

  // Simplified equal height logic
  useEffect(() => {
    if (!comparisionTableRef.current) return

    const ROW_INDEX_ATTR = 'data-row-index'
    const cells = comparisionTableRef.current.querySelectorAll(`[${ROW_INDEX_ATTR}]`)
    const rows = new Map<string, HTMLElement[]>()

    // Group cells by row
    cells.forEach(cell => {
      const htmlCell = cell as HTMLElement
      const rowIndex = cell.getAttribute(ROW_INDEX_ATTR)
      if (rowIndex) {
        // If we haven't seen this row before, create an empty list for it
        if (!rows.has(rowIndex)) {
          rows.set(rowIndex, [] as HTMLElement[])
        }

        // Get the list for this row and add this cell to it
        const rowArray = rows.get(rowIndex)
        if (rowArray) {
          rowArray.push(htmlCell)
        }
      }
    })

    // Apply equal heights
    const AUTO_HEIGHT = 'auto'
    rows.forEach(rowCells => {
      // Reset heights
      rowCells.forEach(cell => (cell.style.height = AUTO_HEIGHT))

      // Find max height
      const maxHeight = Math.max(...rowCells.map(cell => cell.offsetHeight))

      // Apply max height to all cells in row
      rowCells.forEach(cell => (cell.style.height = `${maxHeight}px`))
    })
  }, [columns])

  return (
    <div className={classes}>
      <div className={styles.productcomparisontable} ref={comparisionTableRef}>
        <div className={styles.desktopOnly}>
          <Swiper
            id="product-comparision-table-carousel-new"
            variableWidth
            showNavigator
            numberOfVisibleSlides={numberOfColumns}
            isStickyFreeMode
            navPosition={NAV_POSITION.RIGHT_WITH_PADDING}
            // @i18n-ignore
            tracker={'product-comparision-table-carousel'}
          >
            {columns.map((pc, pcIdx) => (
              <ProductColumn
                column={pc}
                wrapperStyle={{ width: maxWidth }}
                key={pc.id}
                // @i18n-ignore
                tracker={'product-comparision-table'}
                columnIdx={pcIdx}
              />
            ))}
          </Swiper>
        </div>
        <div className={styles.mobileOnly}>
          {columns.map((pc, pcIdx) => (
            <ProductColumn
              column={pc}
              key={pc.id}
              // @i18n-ignore
              tracker={'product-comparision-table'}
              columnIdx={pcIdx}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default ProductComparisonTableNew
